import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { 
  HomeIcon, 
  BuildingOfficeIcon, 
  PlusCircleIcon, 
  UserCircleIcon,
  CalendarIcon,
  ChartBarIcon,
  ArrowDownTrayIcon,
  UsersIcon,
  CogIcon,
  ClipboardDocumentListIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';

const ResponsiveNav = ({ user, onLogout }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isAdmin = user?.role === 'Admin';
  const isManagerOrAdmin = user?.role === 'Admin' || user?.role === 'Manager';

  // Navigation items configuration
  const navItems = [
    { path: '/', label: 'Dashboard', icon: HomeIcon, show: true, end: true },
    { path: '/companies', label: 'Companies', icon: BuildingOfficeIcon, show: true },
    { path: '/journey', label: 'New Company', icon: PlusCircleIcon, show: true },
    { path: '/planning', label: 'Planning', icon: CalendarIcon, show: isManagerOrAdmin },
    { path: '/reports', label: 'Reports', icon: ChartBarIcon, show: true },
    { path: '/downloads', label: 'Downloads', icon: ArrowDownTrayIcon, show: true },
    { path: '/users', label: 'Users', icon: UsersIcon, show: isAdmin },
    { path: '/admin/services', label: 'Services', icon: CogIcon, show: isAdmin },
    { path: '/admin/audit-log', label: 'Audit Log', icon: ClipboardDocumentListIcon, show: isAdmin },
    { path: '/admin/management-console', label: 'Management', icon: Squares2X2Icon, show: isAdmin },
  ].filter(item => item.show);

  return (
    <>
      {/* Desktop Top Navigation Bar - Hidden on mobile */}
      <nav className="hidden md:flex fixed top-0 left-0 right-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 z-50 shadow-sm">
        <div className="w-full px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Brand */}
            <div className="flex items-center gap-3 min-w-fit">
              <img src="/mibot-logo.png" alt="MIBOT" className="h-8 w-8 rounded-lg" />
              <div>
                <h1 className="text-lg font-bold text-gray-900 dark:text-white">MIBOT</h1>
                <p className="text-xs text-blue-600 dark:text-blue-400">Onboarding</p>
              </div>
            </div>

            {/* Navigation Links */}
            <div className="flex items-center gap-1 flex-1 justify-center px-4">
              {navItems.slice(0, 7).map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 whitespace-nowrap ${
                      isActive
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`
                  }
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium text-sm">{item.label}</span>
                </NavLink>
              ))}

              {/* More menu for additional items */}
              {navItems.length > 7 && (
                <div className="relative group">
                  <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all">
                    <Squares2X2Icon className="w-5 h-5" />
                    <span className="font-medium text-sm">More</span>
                  </button>
                  
                  {/* Dropdown */}
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    {navItems.slice(7).map((item) => (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg transition-colors ${
                            isActive ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20' : 'text-gray-700 dark:text-gray-300'
                          }`
                        }
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="font-medium text-sm">{item.label}</span>
                      </NavLink>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* User Profile & Logout */}
            <div className="flex items-center gap-3 min-w-fit">
              <div className="text-right hidden lg:block">
                <p className="text-sm font-medium text-gray-900 dark:text-white">{user?.name}</p>
                <p className="text-xs text-blue-600 dark:text-blue-400">{user?.role}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <button
                onClick={onLogout}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium text-sm transition-colors shadow-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Bottom Navigation - Hidden on desktop */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 pb-safe z-50 shadow-lg">
        <div className="flex justify-around items-center h-16">
          {['/', '/companies', '/journey', '/reports']
            .map((path) => navItems.find((item) => item.path === path))
            .filter(Boolean)
            .map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center flex-1 h-full transition-all duration-200 ${
                  isActive
                    ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                    : 'text-gray-600 dark:text-gray-400'
                }`
              }
            >
              <item.icon className="w-6 h-6 mb-1" />
              <span className="text-xs font-medium">{item.label}</span>
            </NavLink>
          ))}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="flex flex-col items-center justify-center flex-1 h-full text-gray-600 dark:text-gray-400"
          >
            <Squares2X2Icon className="w-6 h-6 mb-1" />
            <span className="text-xs font-medium">More</span>
          </button>
        </div>
      </nav>

      {/* Mobile Full-Screen Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 bg-gray-900/95 backdrop-blur-sm z-50 animate-fadeIn">
          <div className="h-full overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-gray-900 border-b border-gray-700 px-4 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src="/mibot-logo.png" alt="MIBOT" className="h-8 w-8 rounded-lg" />
                <div>
                  <h2 className="text-lg font-bold text-white">Menu</h2>
                  <p className="text-xs text-blue-400">{user?.name} • {user?.role}</p>
                </div>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="text-white text-2xl hover:text-blue-400 transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Menu Items */}
            <div className="p-4 space-y-2">
              {navItems
                .filter((item) => !['/', '/companies', '/journey', '/reports'].includes(item.path))
                .map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.end}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-4 p-4 rounded-xl transition-all duration-200 ${
                        isActive
                          ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      }`
                    }
                  >
                    <item.icon className="w-6 h-6" />
                    <span className="font-medium">{item.label}</span>
                  </NavLink>
                ))}

              {/* Logout in Mobile Menu */}
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  onLogout();
                }}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 transition-colors mt-6"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spacer for fixed navigation */}
      <div className="hidden md:block h-16" />
      <div className="md:hidden h-16" />
    </>
  );
};

export default ResponsiveNav;

